<template>
  <div v-if="totalMenuItems > 0">
    <!-- Single menu item when there's only one contract and no "All events" option -->
    <template v-if="totalMenuItems === 1">
      <CSidebarNavItem
          :name="$pgettext('event.contract_select.title', 'Events')"
          icon="cip-calendar"
          :to="getRouteConfig(singleContract.id)"
          @click.native="onContractChange(singleContract)"
      />
    </template>

    <!-- Menu with submenu items when there are multiple items -->
    <template v-else>
      <CSidebarNavItem
          :name="$pgettext('event.contract_select.title', 'Events')"
          icon="cip-calendar"
          :class="['c-sub-nav-item-toggle', {'on': toggle}]"
          @click.native="toggle=!toggle"
      />

      <!-- "All the events" as a separate item -->
      <div class="c-sub-items" v-if="toggle">
        <CSidebarNavItem
            v-if="hasOtherContracts"
            :name="$pgettext('event.contract_select.show_all', 'All the events')"
            :class="['c-sub-nav-item', {'c-active': selectedContract === -1}]"
            icon="cip-check"
            :to="{ name: 'eventList', params: { contractId: 'all' } }"
            @click.native="onContractChange({id: -1, name: $pgettext('event.contract_select.show_all', 'All the events')})"
            :data-debug="`All events - selectedContract: ${selectedContract}, active: ${selectedContract === -1}`"
        />

        <!-- Contract items -->
        <CSidebarNavItem
            v-for="item in contracts"
            :key="item.id"
            :name="item.name"
            :class="['c-sub-nav-item', {'c-active': isContractActive(item.id)}]"
            icon="cip-check"
            :to="getRouteConfig(item.id)"
            @click.native="onContractChange(item)"
            :data-debug="`item.id: ${item.id}, selectedContract: ${selectedContract}, active: ${selectedContract === item.id}`"
        />
      </div>
    </template>
    <CSidebarNavItem
        :name="$pgettext('event.contract_select.new', 'New event')"
        icon="cip-plus"
        :class="['c-nav-item-new-event']"
        v-if="canCreateEvent"
        @click.native="openCreateNewEvent"
    />
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import eventBus from '@/domain/core/constant/eventBus'

export default {
  name: 'Events',
  data() {
    return {
      toggle: true
    }
  },
  computed: {
    ...mapGetters('events', ['contract', 'contracts', 'hasOtherContracts', 'canCreateNew']),
    selectedContract() {
      const contractId = this.contract ? this.contract.id : -1
      return contractId
    },
    canCreateEvent() {
      if (!this.hasPerm('events.create')) return false
      return this.canCreateNew
    },
    singleContract() {
      // Return the appropriate contract when there's only one item
      if (this.contracts.length > 0) {
        return this.contracts[0]
      } else if (this.hasOtherContracts) {
        // Return a default contract with id -1 and name 'events' when there's only 'other'
        return {id: -1, name: this.$pgettext('event.contract_select.title', 'Events')}
      }
      return null
    },
    totalMenuItems() {
      // Count contracts plus "All the events" if hasOtherContracts is true
      return this.contracts.length + (this.hasOtherContracts ? 1 : 0)
    }
  },
  methods: {
    ...mapActions('events', ['fetchContracts', 'setActiveContract']),
    isContractActive(itemId) {
      const selected = this.selectedContract
      const isActive = selected === itemId || String(selected) === String(itemId)
      return isActive
    },
    async onContractChange(contract) {
      await this.setActiveContract(contract.id)
    },
    openCreateNewEvent() {
      const parent = this.getParentWithMethod('openCreateNewEvent')
      if (parent) parent.openCreateNewEvent({
        canClose: true,
        isFirstEvent: false,
        contract: null,
      })
      else this.notifyError({text: 'Parent not found'})
    },
    getRouteConfig(contractId) {
      // If contractId is -1, use 'all' as the param
      if (contractId === -1) {
        return {name: 'eventList', params: {contractId: 'all'}}
      }
      // Otherwise, include contractId in the route params
      return {
        name: 'eventList',
        params: {contractId: contractId.toString()}
      }
    }
  },
  async created() {
    // Only fetch contracts if we don't have them yet
    if (!this.contracts || this.contracts.length === 0) {
      await this.fetchContracts()
    }
  },
  watch: {
    contract: {
      handler(newVal, oldVal) {
      },
      immediate: true
    },
    '$route': {
      handler(newRoute, oldRoute) {
        // Handle route sync for eventList
        if (newRoute.name === 'eventList' && newRoute.params?.contractId) {
          const routeContractId = newRoute.params.contractId
          
          // If route has 'all', set contract to -1
          if (routeContractId === 'all') {
            if (this.contract?.id !== -1) {
              this.setActiveContract(-1)
            }
          } 
          // If route has a specific contractId, sync with store if different
          else if (routeContractId) {
            const routeContractIdNum = parseInt(routeContractId)
            if (this.contract?.id !== routeContractIdNum) {
              this.setActiveContract(routeContractIdNum)
            }
          }
        }
      },
      immediate: true
    }
  }
}
</script>
